import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslatedToastrService } from './translated-toastr.service';
import { GlobalErrorHandler } from '../handlers/global-error-handler';

@Injectable({
    providedIn: 'root',
})
export class ValidationHandlerService {
    constructor(
        private translatedToastrService: TranslatedToastrService,
        private globalErrorHandler: GlobalErrorHandler
    ) {}

    public checkFormValid(formGroup: FormGroup): boolean {
        if (!formGroup.valid) {
            this.forceShowErrorMessages(formGroup);
            this.isValidAndMarkControlsTouchedIfInvalid();
            this.scrollToFirstInvalidControl();
        }
        return formGroup.valid;
    }

    private scrollToFirstInvalidControl() {
        const firstInvalidControl: HTMLElement = document.querySelector('form .ng-invalid');

        window.scroll({
            top: this.getTopOffset(firstInvalidControl),
            left: 0,
            behavior: 'smooth',
        });
    }

    private getTopOffset(controlEl: HTMLElement): number {
        const labelOffset = 100;
        return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
    }

    private forceShowErrorMessages(controlListContainer: any) {
        Object.keys(controlListContainer.controls).forEach(field => {
            const control = controlListContainer.get(field);
            if (control instanceof FormGroup) {
                this.forceShowErrorMessages(control);
            } else if (control instanceof FormArray) {
                this.forceShowErrorMessages(control);
            } else {
                control.markAsTouched({ onlySelf: true });
            }
        });
    }

    public handleServerError(error: any, formGroup?: FormGroup) {
        if (
            (error.name && error.name === 'EmptyError') ||
            (error.message && error.message == "Cannot read properties of null (reading 'parentNode')")
        ) {
            return;
        }

        if (error && error.isValidationError) {
            if (formGroup) {
                this.handleValidationErrors(formGroup, error.validationErrors);
            }
            this.displayMessages(error);
        } else if (error.status == 400) {
            //bad request, isSuccess false BaseResponse with message only
            this.translatedToastrService.error(error.message, 'common.error', {}, error.additionalInformation);
        } else {
            this.globalErrorHandler.handleError(error);
        }
    }

    public displayMessages(baseResponse: any): void {
        if (baseResponse.message) {
            this.translatedToastrService.success(
                baseResponse.message,
                'common.success',
                {},
                baseResponse.additionalInformation
            );
        }
        if (baseResponse.successWarningMessage) {
            this.translatedToastrService.warning(
                baseResponse.successWarningMessage,
                'common.warning',
                {},
                baseResponse.additionalInformation
            );
        }
        if (baseResponse.successErrorMessage) {
            this.translatedToastrService.error(
                baseResponse.successErrorMessage,
                'common.error',
                {},
                baseResponse.additionalInformation
            );
        }
    }

    private handleValidationErrors(formGroup: FormGroup, validationErrors: any) {
        for (const property in validationErrors) {
            const formControlName = this.toCamelCase(property);
            let eModel: any = {};
            if (formControlName.indexOf('[') == -1) {
                if (formGroup.get(formControlName)) {
                    for (const i in validationErrors[property]) {
                        eModel[validationErrors[property][i]] = true;
                    }

                    (formGroup.get(formControlName) as FormControl).setErrors(eModel);
                    (formGroup.get(formControlName) as FormControl).markAsTouched();
                } else {
                    alert('FormControl not found: ' + formControlName);
                }
            } else {
                //listItems[1].propertyNameCamelCase -> [listItems, 1, propertyNameCamelCase]
                let nestedcontrolNames = formControlName.split(/[.\[]+/);
                for (let i: number = 0; i < nestedcontrolNames.length; i++) {
                    nestedcontrolNames[i] = nestedcontrolNames[i].replace(']', '');
                }
                let formControl = formGroup.get(nestedcontrolNames[0]);
                for (let i: number = 1; i < nestedcontrolNames.length; i++) {
                    formControl = (formControl as FormControl).get(nestedcontrolNames[i]);
                }
                eModel[validationErrors[property]] = true;
                (formControl as FormControl).setErrors(eModel);
                (formControl as FormControl).markAsTouched();
            }
        }

        this.isValidAndMarkControlsTouchedIfInvalid();
    }

    public isValidAndMarkControlsTouchedIfInvalid(): void {
        if (document.querySelectorAll('.form-control.ng-invalid').length > 0) {
            (<HTMLElement>document.querySelectorAll('.form-control.ng-invalid')[0]).focus();
        } else if (document.querySelectorAll('multiselect-dropdown-input.ng-invalid').length > 0) {
            (<HTMLElement>(
                document
                    .querySelectorAll('multiselect-dropdown-input.ng-invalid')[0]
                    .querySelector('div.multiselect-dropdown')
            )).focus();
        } else {
            if (document.querySelectorAll('.ng-invalid').length > 0) {
                (<HTMLElement>document.querySelectorAll('.ng-invalid')[0].querySelector('.ng-invalid')).focus();
            }
        }
    }

    private toCamelCase(data: string): string {
        const list = Array.isArray(data) ? data : data.split('.');
        for (let i: number = 0; i < list.length; i++) {
            list[i] = list[i].charAt(0).toLowerCase() + list[i].slice(1);
        }
        return list.join('.');
    }
}
